<script setup lang="ts">
import { computed, inject, useTemplateRef } from "vue";
import { useRouter } from "vue-router";

import skoreLogo from "@/assets/images/skore-text.svg";
import BreadcrumbNavigation, {
  type BreadcrumbItem,
} from "@/components/ui/BreadcrumbNavigation.vue";
import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownButtonItem from "@/components/ui/DropdownButtonItem.vue";
import UserAvatar from "@/components/ui/UserAvatar.vue";
import { getUserFriendlyName } from "@/dto";
import { lastErrorKey } from "@/injectionKeys";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { useAuthStore } from "@/stores/auth";
import { useModalsStore } from "@/stores/modals";

const { breadcrumbItems } = defineProps<{ breadcrumbItems: BreadcrumbItem[] }>();

const api = useApi();
const router = useRouter();
const modals = useModalsStore();
const auth = useAuthStore();

const lastError = inject(lastErrorKey);

const dropdown = useTemplateRef<InstanceType<typeof DropdownButton>>("dropdown");
const links = [
  {
    name: "Skore Hub Documentation",
    icon: "icon-book",
    href: "https://probabl.notion.site/skore-hub",
  },
  {
    name: "Skore on GitHub",
    icon: "icon-github",
    href: "https://github.com/probabl-ai/skore",
  },
  {
    name: "Community Support",
    icon: "icon-discord",
    href: "https://discord.probabl.ai",
  },
  {
    name: "Request Professional Support",
    icon: "icon-buoy",
    href: "https://hello.probabl.ai/contact-us-0?utm_source=skore_hub&utm_medium=inapp&utm_campaign=pro_support",
  },
];

const myName = computed(() => (auth.currentUser ? getUserFriendlyName(auth.currentUser) : ""));

function onLinkClick(href: string) {
  window.open(href, "_blank")?.focus();
}

function onLogout() {
  router.push({ name: RouteNames.Logout });
}

async function onAbout() {
  const liveness = await api.fetchLiveness();
  const message = `
    Skore Hub<br/>
    <br/>
    UI version: ${import.meta.env.VITE_VUE_BUILD_VERSION?.slice(0, 7) ?? "unknown"}<br/>
    API version: ${liveness?.version ?? "dev"}.${liveness?.hash_commit?.slice(0, 7) ?? "unknown"}<br/>
    <br/>
    Copyright © ${new Date().getFullYear()} Probabl<br/>
    All rights reserved.<br/>
    `;
  modals.alert({ title: "About", message });
}
</script>

<template>
  <div class="app-toolbar">
    <div class="logo" @click="router.push({ name: RouteNames.Dispatch })">
      <img :src="skoreLogo" alt="skore" />
    </div>
    <nav>
      <BreadcrumbNavigation v-if="!lastError" :items="breadcrumbItems" />
    </nav>
    <div class="profile">
      <UserAvatar :name="myName" @click.stop="dropdown?.toggle()" />
      <DropdownButton
        icon-left="icon-chevron-up-down"
        ref="dropdown"
        kind="ghost"
        :is-inline="true"
        :allowed-placements="['bottom-end']"
      >
        <DropdownButtonItem icon-left="icon-info-circle-2" label="About" @click="onAbout" />
        <DropdownButtonItem
          v-for="link in links"
          :key="link.name"
          :icon-left="link.icon"
          :label="link.name"
          @click="onLinkClick(link.href)"
        />
        <DropdownButtonItem
          icon-left="icon-user"
          label="Your Account"
          @click="router.push({ name: RouteNames.Account })"
        />
        <DropdownButtonItem icon-left="icon-logout" label="Logout" @click="onLogout" />
      </DropdownButton>
    </div>
  </div>
</template>

<style scoped>
.app-toolbar {
  display: flex;
  width: 100%;
  height: var(--numbers-56);
  flex-direction: row;
  padding: 0 var(--page-padding);
  border-bottom: solid var(--stroke-width-md) var(--border-tertiary);
  background-color: var(--background-primary);

  .logo {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    & > img {
      height: var(--numbers-32);
      transform: translateY(1px);
    }
  }

  nav {
    display: flex;
    overflow: hidden;
    max-width: 100%;
    flex: 1;
    flex-direction: row;
    align-items: center;
    margin-left: var(--numbers-8);
  }

  .profile {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 0 0 0 var(--numbers-8);
    border-radius: var(--numbers-12);
    margin: var(--numbers-4) 0;
    cursor: pointer;
    transition: background-color var(--animation-duration) var(--animation-easing);

    &:hover {
      background-color: var(--background-secondary-hover);
    }

    :deep(.button) {
      padding: 0 var(--numbers-8) 0 0;
      border: none;
    }

    :deep(.items) {
      margin-top: var(--numbers-6);
    }
  }
}
</style>
